import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { CryptoInterceptor } from './CryptoInterceptor';

/**
 * Cliente API base para realizar peticiones HTTP
 */
export class ApiClient {
  private client: AxiosInstance;
  private baseURL: string = process.env.REACT_APP_API_URL || '';
  private cryptoInterceptor: CryptoInterceptor;

  constructor() {
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY
      }
    });
    
    // Inicializar el interceptor de encriptación
    this.cryptoInterceptor = new CryptoInterceptor();
    
    // Configurar interceptores para encriptar/desencriptar automáticamente
    this.client.interceptors.request.use(
      this.cryptoInterceptor.requestInterceptor,
      error => Promise.reject(error)
    );
    
    this.client.interceptors.response.use(
      this.cryptoInterceptor.responseInterceptor,
      this.cryptoInterceptor.errorInterceptor
    );
  }

  /**
   * Realiza una petición GET
   * @param url - Ruta de la API
   * @param config - Configuración opcional para la petición
   * @returns Promesa con la respuesta
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  /**
   * Realiza una petición POST
   * @param url - Ruta de la API
   * @param data - Datos a enviar
   * @param config - Configuración opcional para la petición
   * @returns Promesa con la respuesta
   */
  async post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * Realiza una petición PUT
   * @param url - Ruta de la API
   * @param data - Datos a enviar
   * @param config - Configuración opcional para la petición
   * @returns Promesa con la respuesta
   */
  async put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  /**
   * Realiza una petición PATCH
   * @param url - Ruta de la API
   * @param data - Datos a enviar
   * @param config - Configuración opcional para la petición
   * @returns Promesa con la respuesta
   */
  async patch<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * Realiza una petición DELETE
   * @param url - Ruta de la API
   * @param config - Configuración opcional para la petición
   * @returns Promesa con la respuesta
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
} 